/**
 * Every sequence editor shortcut (1.7.2–1.7.4), grouped by area: the editor's Keys tab, the Help guide's keys tables
 * and (the rows marked `main`) the Editor group of the global ? window read this one list, so they never disagree.
 */
import { SUBTITLE_KEY_GROUP } from '../../subtitles/keys';

export interface EditorKey {
  label: string;
  keys: string[];
  /** Shown in the ? window's Editor group too. */
  main?: boolean;
}

export interface EditorKeyGroup { area: string; rows: EditorKey[] }

export const EDITOR_KEY_GROUPS: EditorKeyGroup[] = [
  { area: 'Playback', rows: [
    { label: 'Play / pause the sequence', keys: ['Space'], main: true },
    { label: 'Shuttle back / stop / forward (again for 2× and 4×)', keys: ['J', 'K', 'L'], main: true },
    { label: 'Step a frame / a second', keys: ['←', '→', '⇧←', '⇧→'] },
    { label: 'Start / end (in the source window, End on a recording goes live and follows it)', keys: ['Home', 'End'] },
    { label: 'Previous / next marker', keys: ['⇧↑', '⇧↓'] },
    { label: 'Go to a timecode', keys: ['click the timecode'] },
  ] },
  { area: 'Marking and three-point', rows: [
    { label: 'Sequence In / Out (sequence viewer or timeline active; in the source viewer they mark the source)', keys: ['I', 'O'], main: true },
    { label: 'Go to the sequence In / Out', keys: ['⇧I', '⇧O'] },
    { label: 'Clear the sequence In and Out', keys: ['⌥X'] },
    { label: 'Insert the source at the playhead or the sequence In (the targeted tracks move later)', keys: [','], main: true },
    { label: 'Overwrite with the source (nothing moves)', keys: ['.'], main: true },
    { label: 'Match frame: open the clip under the playhead in the source viewer at the same frame', keys: ['F'], main: true },
    { label: 'Add a marker (in the source viewer M mutes the source)', keys: ['M'] },
  ] },
  { area: 'Tools and trimming', rows: [
    { label: 'Select, Slip and Slide tools (Option-drag a clip to slip it with any tool)', keys: ['V', 'Y', 'U'], main: true },
    { label: 'Ripple and Roll tools', keys: ['B', '⇧B'], main: true },
    { label: 'Blade (⇧-click splits every track) and Select forward (⇧-click: that track only)', keys: ['C', 'A'], main: true },
    { label: 'Back to the Select tool', keys: ['Esc'] },
    { label: 'Split the selected clips (or V1) at the playhead', keys: ['S'], main: true },
    { label: 'Split every track at the playhead', keys: ['⌘K'], main: true },
    { label: 'Trim a V1 clip / trim and ripple (any track)', keys: ['drag an edge', '⌘ drag'] },
    { label: 'Slip the selected clip a frame / ten frames', keys: ['⌥,', '⌥.', '⇧⌥,', '⇧⌥.'] },
    { label: 'Transition at the selected clip or the nearest cut (the menu’s kind)', keys: ['T'] },
  ] },
  { area: 'Editing', rows: [
    { label: 'Delete, leaving a gap / ripple delete (Sync moves the other tracks too)', keys: ['Delete', '⇧Delete'], main: true },
    { label: 'Drag a clip on V1: overwrite where it lands / insert', keys: ['drag', '⌘ drag'] },
    { label: 'Drag a picture up or down between V1 and the video tracks (its sound follows)', keys: ['drag'] },
    { label: 'Undo / redo', keys: ['⌘Z', '⇧⌘Z'], main: true },
    { label: 'Add a reframe key at the playhead (Reframe lane) / previous, next key', keys: ['R', '[', ']'] },
  ] },
  { area: 'Audio', rows: [
    { label: 'Add a volume key on the level line', keys: ['⌘ click'] },
    { label: 'Remove the selected volume key', keys: ['Delete'] },
    { label: 'Change the clip’s level (drag the line) / move a key', keys: ['drag'] },
    { label: 'Crossfade into the selected clip from the one before', keys: ['⇧⌘D'], main: true },
  ] },
  { area: 'Selection and clipboard', rows: [
    { label: 'Select several (or drag a box on empty track space); Option-click one of a linked group', keys: ['⇧ click', '⌘ click', '⌥ click'] },
    { label: 'Select all / clear the selection', keys: ['⌘A', 'Esc'], main: true },
    { label: 'Cut / copy / paste at the playhead / paste and insert', keys: ['⌘X', '⌘C', '⌘V', '⇧⌘V'], main: true },
    { label: 'Link / unlink (unlinking a V1 clip puts its sound on an audio track)', keys: ['⌘L', '⇧⌘L'] },
    { label: 'Clip menu (or right-click)', keys: ['⇧F10'] },
  ] },
  { area: 'Tracks', rows: [
    { label: 'Source V and Source A in the track headers: where Insert and Overwrite put picture and sound', keys: ['click'] },
    { label: 'Mute / solo a sound track', keys: ['M', 'S in the header'] },
  ] },
  { area: 'View', rows: [
    { label: 'Snapping on / off', keys: ['N'] },
    { label: 'Zoom to fit / in / out', keys: ['\\', '=', '−', '⌃ wheel'] },
  ] },
  // 1.9.0: the subtitle keys (subtitles/keys.ts), the same in the asset Subtitles tab.
  SUBTITLE_KEY_GROUP,
];

/** The editor's side tabs (1.7.4: Keys; the Audio tab became the Meters pane, so a saved 'audio' reads as the Inspector; 1.8.0: Rights, while rights are on). */
export type SideTab = 'inspector' | 'subtitles' | 'exports' | 'details' | 'keys' | 'rights';
export const SIDE_TABS: readonly SideTab[] = ['inspector', 'subtitles', 'exports', 'details', 'keys', 'rights'];
/** A saved editor.side: one of the tabs, else (an old 'audio', anything unknown) the Inspector. */
export const sideTabOf = (v: unknown): SideTab => ((SIDE_TABS as readonly unknown[]).includes(v) ? (v as SideTab) : 'inspector');
